import Vue from "vue";
import Vuex from "vuex";
import lodash from 'lodash';
import deepdash from 'deepdash';
const _ = deepdash(lodash);

Vue.use(Vuex);

import commons from "../commons/commons";
import { authorizationStore } from "./authorizationStore";
import { commonStore } from "./commonStore";
import moduleGuard from "../commons/moduleGuard";
import selectionCache from "./selectionCache";
import * as canvasOps from "../../business/canvasOps";

import VueCookies from 'vue-cookies'
import LocalStorageService from "./LocalStorageService";
import webAppConfig from "../commons/webApp.config";
Vue.use(VueCookies)

const getInitialState = () => ({
    ...commonStore.state,
    modules: {},
    modulePromises: {},
    users: [],
    states: [],
    selection: { type: '', item: null },
    baseServer: null,
    vector: null,
    originalVector: null,
    vectorGeneration: 0,
    workspace: null,
    originalWorkspace: null,
    vectorId: null,
    list: null,
    filterText: '',
    filterKeyword: null,
    suggestions: [],
    canvas: {
        information: [],
        states: [],
        flows: [],
        activities: [],
        menus: [],
        actions: [],
        documents: [],
        designer: {},
    },
    isSaving: false,
    autoPush: true,
    forceSubmit: false,
    /* D12: the canvas-ops engine's ledger — apply records (with per-op inverses) for
       guarded deletes and, later, AI proposals. Draft-scoped: reset on module load. */
    applyHistory: [],
});

/* SS06419 (Automated Web Tests (Lite) #603): the workflow drawer saved the whole module once per new state without
   waiting, and every POST /api/studio carried the audit.updatedDate the client had last acknowledged. The server
   refuses a save whose updatedDate is more than 5 s off the stored one ("Concurrency conflict") and otherwise lets
   the last write win, so a burst of saves lost states while the drawer showed them. Ruling (Ke Yeng, 2026-10-25):
   one save per module in flight, here in the store, for every caller. A save asked for while another is running
   waits; saves asked for meanwhile go as one, with the latest canvas (latest wins), and it sends the updatedDate
   the previous save acknowledged. A module without an id is still being created and is not queued. */
const vectorSaveQueues = {};

async function sendVectorSave(context, payload, request, queue) {
    const { liveVectorAtRequest, submittedVector, submittedVectorGeneration } = request;
    const submittedModuleId = submittedVector && submittedVector.id;
    let vectorToSubmit = _.cloneDeep(submittedVector);

    const acknowledged = queue && queue.updatedDate;
    const snapshotDate = vectorToSubmit && vectorToSubmit.audit && vectorToSubmit.audit.updatedDate;
    if (acknowledged && (!snapshotDate || new Date(acknowledged) > new Date(snapshotDate))) {
        vectorToSubmit.audit = { ...(vectorToSubmit.audit || {}), updatedDate: acknowledged };
    }

    const lists = ['actions', 'documents', 'flows', 'logicServices',
        'menus', 'notifications', 'states', 'triggers',
        'authorizations'];

    const informationList = ['connectionField', 'connectionId',
        'description', 'extra', 'displayKey', 'uniqueKey'];

    const toRemoveList = ['connectionModel', 'connectionFieldModel']

    if (vectorToSubmit.extension['information']) {
        vectorToSubmit.extension['information'].forEach(row => {
            informationList.forEach(field => {
                if (row[field] == null || row[field] == false) {
                    delete row[field];
                }
            });

            toRemoveList.forEach(field => {
                delete row[field];
            });

            if (row.fields) {
                row.fields.forEach(tableField => {
                    informationList.forEach(columnField => {
                        if (tableField[columnField] == null || tableField[columnField] == false) {
                            delete tableField[columnField];
                        }
                    });

                    toRemoveList.forEach(field => {
                        delete tableField[field];
                    });
                });
            }

            delete row['columns'];
        });
    }

    lists.forEach(fieldList => {
        if (vectorToSubmit.extension[fieldList] == null
            || vectorToSubmit.extension[fieldList].length == 0) {
            delete vectorToSubmit.extension[fieldList];
        }
    });

    vectorToSubmit.dynamic.expansion = JSON.stringify(vectorToSubmit.extension);
    //delete vectorToSubmit.extension;

    vectorToSubmit.entityHandler = {
        manyToManyCollectionsToSave: ['Workspaces']
    };

    let payloadToSubmit = {
        vector: vectorToSubmit,
        activityId: payload && payload.activityId,
        description: payload && payload.description,
        newVersion: payload && payload.newVersion,
        forceBypassConsistencyCheck: payload && payload.forceSubmit,
    }

    if (context.state.autoPush === false) {
        payloadToSubmit.autoPush = false;
    }

    let result = await context.state.client.post('/api/studio', payloadToSubmit);
    if (queue && result && result.data && result.data.updatedDate != null) {
        queue.updatedDate = result.data.updatedDate;
    }

    // Drop this tab's cached dropdown option lists for the saved module (SS05926). The
    // SignalR `workspaceChanged` push covers every OTHER client, but not necessarily this
    // one: Client.vue only subscribes outside builderMode, and `autoPush: false` suppresses
    // the push entirely — so the designer would otherwise be the one person still seeing
    // the pre-save Display Information.
    selectionCache.invalidateDesign(result.data.id != null ? result.data.id : submittedModuleId);

    const currentVector = context.state.vector;
    const currentModuleId = currentVector && currentVector.id;
    const isCurrentGeneration = context.state.vectorGeneration === submittedVectorGeneration;
    const isCurrentModule = isCurrentGeneration && (
        submittedModuleId == null
            ? currentVector === liveVectorAtRequest
            : currentModuleId != null && currentModuleId.toString() === submittedModuleId.toString()
    );

    if (isCurrentModule) {
        context.commit('acknowledgeVectorSave', {
            vector: submittedVector,
            id: result.data.id,
            updatedDate: result.data.updatedDate,
        });
        context.commit('setWorkspaceModule', context.state.vector);
    }

    return result;
}

/* SS06418: loadVector commits only the answer to the latest load, and only while no other module was committed or
   reset since it was asked for (opening State while Staff was still loading let Staff's late answer take over). */
let vectorLoadSequence = 0;

export const store = new Vuex.Store({
    state: getInitialState(),
    mutations: {
        resetState(state) {
            Object.assign(state, getInitialState());
        },
        setClientHeadersWsId(state, data) {
            state.client.defaults.headers['wsId'] = data;
        },
        setIsSaving(state, data) {
            state.isSaving = data;
        },
        updateModule(state, data) {
            state.vector = data;
        },
        setCanvas(state, data) {
            state.canvas = data;
        },
        setVector(state, data) {
            state.vectorId = data;
        },
        setWorkspaceModule(state, data) {
            if (state.workspace) {
                try {
                    let workspaceModule = state.workspace.vectors.find(x => x.id == data.id);
                    if (workspaceModule) {
                        workspaceModule.name = data.name;
                        workspaceModule.emoji = data.emoji;
                    }
                } catch(err) { console.error(err); }

                try {
                    let workspaceMenu = _.findDeep(state.workspace.menus, x => (x.id == data.id), { childrenPath: 'children' });
                    if (workspaceMenu && workspaceMenu.value) {
                        workspaceMenu.value.name = data.name;
                        workspaceMenu.value.emoji = data.emoji;
                        workspaceMenu.value.isProfile = data.isUserCollection;
                        workspaceMenu.value.published = data.published;
                    }
                } catch(err) { console.error(err); }
            }
        },
        commitVector(state, data) {
            state.vectorGeneration += 1;

            if (data == null || state.vectorId != data.id) {
                /* Releasing the cross-module reference cache on module switch/reset.
                   findVariable() caches the full payload of every referenced module
                   and otherwise retains them for the whole studio session. Cleared
                   here (not on every save) so it stays effective within a module but
                   is freed once we leave it. */
                state.modules = {};
                state.modulePromises = {};

                if (state.workspace && state.workspace.id) {
                    const preference = LocalStorageService.getStudioPreference(webAppConfig.baseServer, state.workspace.id);

                    if (preference && preference.hasOwnProperty("autoPush")) {
                        state.autoPush = !!preference.autoPush;
                    } else {
                        state.autoPush = true;
                    }

                    state.forceSubmit = !!(preference && preference.forceSubmit);
                } else {
                    state.autoPush = true;
                    state.forceSubmit = false;
                }
            }

            state.vector = data;

            if (state.vector && state.vector.ownerWorkspaceId) {
                state.client.defaults.headers['wsId'] = state.vector.ownerWorkspaceId;
                $cookies.set('wsId', state.vector.ownerWorkspaceId);
                LocalStorageService.setWorkspaceId(webAppConfig.baseServer, state.vector.ownerWorkspaceId);
            }

            if (state.vector && !state.vector.ownerWorkspaceId && state.workspace) {
                state.vector.ownerWorkspaceId = state.workspace.id
            }
            if (data) {
                state.vectorId = data.id;
                state.canvas = data.extension;
                state.originalVector = moduleGuard.compactModule(_.cloneDeep(data));
                if (!state.canvas.triggers) {
                    Vue.set(state.canvas, 'triggers', []);
                }
            } else {
                state.vectorId = null;
                state.canvas = null;
                state.originalVector = null;
            }
            /* fresh draft = fresh ledger (D12: the history is draft-scoped; Save does not
               consume it, but a module load/switch does) */
            state.applyHistory = [];
        },
        commitVectorCanvas(state, data) {
            state.vector.extension = data;
            state.canvas = data;
        },
        commitAutoPush(state, data) {
            state.autoPush = data;
        },
        commitForceSubmit(state, data) {
            state.forceSubmit = data;
        },
        updateOriginal(state, data) {
            state.originalVector = moduleGuard.compactModule(_.cloneDeep(data));
        },
        acknowledgeVectorSave(state, data) {
            const liveVector = state.vector;
            const acknowledgedVector = _.cloneDeep(data.vector);

            if (!liveVector.audit) {
                Vue.set(liveVector, 'audit', {});
            }
            if (!acknowledgedVector.audit) {
                acknowledgedVector.audit = {};
            }

            if (data.updatedDate != null) {
                Vue.set(liveVector.audit, 'updatedDate', data.updatedDate);
                acknowledgedVector.audit.updatedDate = data.updatedDate;
            }
            if (data.id != null) {
                Vue.set(liveVector, 'id', data.id);
                acknowledgedVector.id = data.id;
                state.vectorId = data.id;
            }

            state.originalVector = moduleGuard.compactModule(acknowledgedVector);
        },
        commitWorkspace(state, data) {
            state.workspace = data;
            if (data != null) {
                state.client.defaults.headers['workspace'] = data.name;
                state.client.defaults.headers['wsId'] = data.id;
                state.originalWorkspace = _.cloneDeep({ ...data, menus: commons.trimMenu(state.workspace.menus) });
            } else {
                state.client.defaults.headers['workspace'] = null;
                state.client.defaults.headers['wsId'] = null;
                state.originalWorkspace = null;
            }
            $cookies.set('wsId', data.id);
            LocalStorageService.setWorkspaceId(webAppConfig.baseServer, data.id);
            state.users = data.users;
            state.states = data.states;
            state.filterKeyword = null;
        },
        setWorkspaceName(state, data) {
            state.workspace.name = data;
        },
        commitWorkspaceLastUpdatedOn(state, data) {
            state.workspace.lastUpdatedOn = data;
        },
        commitDiagram(state, data) {
            state.canvas.diagram = data;
        },
        commitUsers(state, data) {
            state.users = data;
        },
        commitList(state, data) {
            state.list = data;
        },
        setBaseServer(state, data) {
            state.baseServer = data;
        },
        removeState(state, targetStateId) {
            let stateToRemove = state.canvas.states
                .find(item => item.id == targetStateId);
            if (stateToRemove) {
                let ind = state.canvas.states.indexOf(stateToRemove);
                state.canvas.states.splice(ind, 1);
            }
        },
        removeFlow(state, { targetFlowId, targetActivityId }) {
            let flow = state.canvas.flows.find(flow => flow.id == targetFlowId);
            if (flow) {
                let ind = state.canvas.flows.indexOf(flow);
                state.canvas.flows.splice(ind, 1);

                let alternativeFlow = state.canvas.flows.find(
                    flow => flow.activityId == targetActivityId
                );
                /* if no alternative, delete the activity */
                if (!alternativeFlow) {
                    let activity = state.canvas.activities.find(
                        activity => activity.id == targetActivityId
                    );
                    if (activity) {
                        let actInd = state.canvas.activities.indexOf(activity);
                        state.canvas.activities.splice(actInd, 1);
                    }
                }
            }
        },
        removeActivity(state, targetActivityId) {
            let flows = state.canvas.flows.filter(flow => flow.activityId == targetActivityId);
            flows.forEach(flow => {
                let flowInd = state.canvas.flows.indexOf(flow);
                state.canvas.flows.splice(flowInd, 1);
            });

            let activity = state.canvas.activities.find(activity =>
                activity.id == targetActivityId);
            if (activity) {
                let ind = state.canvas.activities.indexOf(activity);
                state.canvas.activities.splice(ind, 1);
            }
        },
        removeInformation(state, targetInformationId) {
            let information = state.canvas.information.find(info => info.id == targetInformationId);
            if (information) {
                let ind = state.canvas.information.indexOf(information);
                state.canvas.information.splice(ind, 1);
                /* all activity classification */
                state.canvas.activities.forEach(activity => {
                    if (activity.classification) {
                        let toRemoves = activity.classification.filter(x => x.information.id == targetInformationId);
                        toRemoves.forEach(x => {
                            let cladInd = activity.classification.indexOf(x);
                            activity.classification.splice(cladInd, 1);
                        });
                    }
                });
            }
        },
        commitState(state, locusState) {
            if (!locusState.id) {
                locusState.id = commons.newUUID();
                state.canvas.states.push(locusState);
            } else {
                let originalState = state.canvas.states.find(x => x.id == locusState.id);
                originalState.color = locusState.color;
                originalState.name = locusState.name;
                originalState.description = locusState.description;
            }
        },
        commitActivity(state, { activity, flow }) {
            if (!flow && typeof originalFlow !== 'object')
                flow = {}

            //new activity, new flow
            if (!activity.id && !flow.id) {
                activity.id = commons.newUUID();
                // master plan (2026-08-01): new activities start on the server-native
                // formula engine; only internal/consultant users can relax it later
                if (activity.serverNative === undefined) activity.serverNative = true;
                state.canvas.activities.push(activity);

                flow.id = commons.newUUID();
                flow.activityId = activity.id;
                flow.activity = activity;
                state.canvas.flows.push(flow);
            }
            //new activity, update flow
            else if (!activity.id) {
                activity.id = commons.newUUID();
                if (activity.serverNative === undefined) activity.serverNative = true;
                state.canvas.activities.push(activity);

                let originalFlow = state.canvas.flows.find(x => x.id == flow.id);
                originalFlow.activityId = activity.id;
                originalFlow.activity = activity;
                originalFlow.conditional = flow.conditional;
                originalFlow.conditionalStates = flow.conditionalStates;
            }
            //new flow
            else if (!flow.id) {
                let originalActivity = state.canvas.activities.find(x => x.id == activity.id);
                originalActivity.emoji = activity.emoji;
                originalActivity.color = activity.color;
                originalActivity.cached = activity.cached;

                //update classification
                originalActivity.classification = activity.classification;
                originalActivity.classification.forEach(x => {
                    // x.information = state.canvas.information.find(y => y.id == x.information.id)
                    Object.assign(x, state.canvas.information.find(y => y.id == x.id));
                });

                flow.id = commons.newUUID();
                flow.activityId = activity.id;
                flow.activity = activity;
                state.canvas.flows.push(flow);
            } else {
                let originalActivity = state.canvas.activities.find(x => x.id == activity.id);
                originalActivity.emoji = activity.emoji;
                originalActivity.color = activity.color;
                originalActivity.cached = activity.cached;

                //update classification
                originalActivity.classification = activity.classification;
                originalActivity.classification.forEach(x => {
                    // x.information = state.canvas.information.find(y => y.id == x.information.id);
                    Object.assign(x, state.canvas.information.find(y => y.id == x.id));
                });

                let originalFlow = state.canvas.flows.find(x => x.id == flow.id);
                originalFlow.activityId = activity.id;
                originalFlow.activity = activity;
                originalFlow.toStateId = flow.toStateId;
                originalFlow.conditional = flow.conditional;
                originalFlow.conditionalStates = flow.conditionalStates;
            }

            //remove same activity from same state
            let toBeRemoved = state.canvas.flows.filter(x => x.id != flow.id
                && x.fromStateId == flow.fromStateId
                && x.activityId == activity.id);
            toBeRemoved.forEach(x => {
                let ind = state.canvas.flows.indexOf(x);
                state.canvas.flows.splice(ind, 1);
            });
        },
        commitInformation(state, information) {
            information.fieldName = commons.camelize(information.displayName)
            if (!information.id) {
                information.id = commons.newUUID();
                state.canvas.information.push(information);
            } else {
                let originalInformation = state.canvas.information.find(x => x.id == information.id);
                Object.assign(originalInformation, information);
            }
        },
        commitWorkDesign(state, { workDesign, activityId }) {
            let activity = state.canvas.activities.find(x => x.id == activityId);
            if (activity) {
                activity.workDesign = workDesign;
            }
        },
        commitUpdateActivity(state, { id, classification, design }) {
            let standards = ['create', 'edit', 'createEditView', 'view', 'quickView', 'createUpdate', 'delete', 'changeStatus', 'duplicate', 'comment', 'assign', 'print'];
            let activity = null;

            if (standards.indexOf(id) > -1) {
                activity = state.canvas.standardActivities.find(x => x.id == id);
            } else {
                activity = state.canvas.activities.find(x => x.id == id);
            }

            if (activity) {
                activity.design = design;
                activity.classification = classification;
            }
        },
        filterElement(state, keyword) {
            state.filterKeyword = keyword;
        },
        suggestions(state, payload) {
            state.suggestions = payload;
        },
        commitUpdateAuthorization(state, payload) {
            if (!state.canvas.authorizations)
                Vue.set(state.canvas, 'authorizations', [])

            state.canvas.authorizations = payload
        },
        setVectorTrigger(state, data) {
            let triggers = state.vector.extension.triggers;
            if (data.id) {
                const findIndex = triggers.findIndex((x) => x.id == data.id);
                if (findIndex > -1) {
                    Vue.set(triggers, findIndex, data);
                } else {
                    triggers.push({...data});
                }
            } else {
                triggers.push({
                    id: commons.newUUID(),
                    ...data,
                });
            }
        },
        removeVectorTrigger(state, id) {
            let triggers = state.vector.extension.triggers;
            const findIndex = triggers.findIndex(x => x.id == id);
            if (findIndex > -1) {
                triggers.splice(findIndex, 1);
            }
        },
        pushApplyRecord(state, record) {
            state.applyHistory.push(record);
            if (state.applyHistory.length > 50) state.applyHistory.shift();
        },
        clearApplyHistory(state) {
            state.applyHistory = [];
        },
        registerModule(state, module) {
            state.modules[module.id] = module;
        },
        setModulePromise(state, { moduleId, promise }) {
            state.modulePromises[moduleId] = promise;

        }
    },
    getters: {
        isSaving: state => state.isSaving,
        uploadFormData: (state) => (payload) => {
            return {
                vectorId: state.vectorId,
                fieldName: payload.fieldName,
                fieldId: payload.fieldId,
                type: payload.formType
            };
        },
        workspaceId: state => state.workspace && state.workspace.id,
        workspaceName: state => state.workspace && state.workspace.name,
        workspaceCountry: state => state.workspace && state.workspace.country,
        workspaceBeta: state => !authorizationStore.getters.demoMode && Boolean(state.workspace && state.workspace.beta),
        workspaceModules: state => (state.workspace && state.workspace.vectors) || [],
        workspaceStates: state => state.workspace && state.workspace.states || [],
        workspaceUsers: state => state.workspace && state.workspace.users || [],
        isUserCollection: state => state.vector && state.vector.isUserCollection,
        originalWorkspaceMenus: state => state.originalWorkspace && state.originalWorkspace.menus,
        vector: state => state.vector || {},        
        vectorTriggers: state => state.vector && state.vector.extension && state.vector.extension.triggers || [],
        canvasInformation: state => state.canvas && state.canvas.information || [],
        // Real views only. The legacy "defaultListing" placeholder row is a design carrier, not a
        // view (docs/listing-default-listing-design-carrier-proposal.md, R13): the server hides it
        // from every listing surface and lifts its design into canvas.listDesign on save, and the
        // client keeps it in canvas.menus until the Phase 2 app is the floor (R9) — so every design
        // surface reads views through here, never the raw array.
        canvasMenus: state => (state.canvas && state.canvas.menus || [])
            .filter(x => !(x && x.id === 'defaultListing' && x.default === true)),
        canvasHasViews: (state, getters) => getters.canvasMenus.length > 0,
        // The module-level listing design: what the default view (a module with no views) renders
        // with, and what a view without a card of its own inherits.
        canvasListDesign: state => state.canvas && state.canvas.listDesign || null,
        canvasDocuments: state => state.canvas && state.canvas.documents || [],
        canvasActivities: state => state.canvas && state.canvas.activities || [],
        canvasStandardActivities: state => state.canvas && state.canvas.standardActivities || [],
        canvasStates: state => state.canvas && state.canvas.states || [],
        canvasFlows: state => state.canvas && state.canvas.flows || [],
        canvasActions: state => state.canvas && state.canvas.actions || [],
        canvasLogicServices: state => state.canvas && state.canvas.logicServices || [],
        canvasImport: state => state.canvas && state.canvas.import,
        autoPush: state => state.autoPush === false ? false : true,
        forceSubmit: state => state.forceSubmit === true,
        /* D14 alpha gate for the canvas-ops surfaces (guarded deletes, apply history).
           Mirrors the client GNey gate (clientStore.js:909): internal identities now;
           workspace.gney self-activates once the D6 studio workspace payload carries it.
           The engine itself is ungated library code — only surface wiring checks this. */
        canvasOpsEnabled: state =>
            !authorizationStore.getters.demoMode &&
            (authorizationStore.getters.isInternalIdentity ||
                Boolean(state.workspace && state.workspace.gney)),
        applyHistory: state => state.applyHistory
    },
    actions: {
        ...commonStore.actions,
        /* ---- canvas-ops engine (P1 UOA core) ---------------------------------------- */
        /* Compose the D11 guard checklist for a pending remove. Pure read — the dialog
           renders the rows; nothing mutates until guardedRemoveApply. */
        guardedRemoveCompose({ state }, { address }) {
            return canvasOps.composeCascade(state.canvas, address);
        },
        /* THE apply path (D13: local, guard, and AI ops all land here). Validates the ops
           through the full pipeline; on success swaps the canvas wholesale
           (clone-apply-swap — Vue2 reactivity) and appends the D12 apply record.
           `authRows` (guard lane only): authorization rows the human checked for removal —
           engine-untouchable per D8, so they are removed from a working clone BEFORE the
           pipeline runs (otherwise the sweep still sees them and refuses as manual). A
           refusal discards the clone, auth removal included — atomicity holds. */
        applyCanvasOps({ state, commit }, { ops, source, label, authRows }) {
            let base = state.canvas;
            const removedAuth = [];
            if (authRows && authRows.length && Array.isArray(base.authorizations)) {
                base = _.cloneDeep(state.canvas);
                for (const row of authRows) {
                    const index = base.authorizations.findIndex(a => _.isEqual(a, row));
                    if (index > -1) {
                        removedAuth.push({ row: _.cloneDeep(row), index });
                        base.authorizations.splice(index, 1);
                    }
                }
            }

            const result = canvasOps.applyProposal(base, ops, { source, label });
            if (!result.ok) return result;
            if (removedAuth.length) result.record.authRows = removedAuth;

            commit('commitVectorCanvas', result.canvas);
            commit('pushApplyRecord', result.record);
            return result;
        },
        /* Build the revert checklist for a record: inverse ops + written-vs-current
           conflict rows (D12). The caller applies the checked subset via applyCanvasOps. */
        composeRevert({ state }, { record }) {
            return canvasOps.buildRevertProposal(state.canvas, record);
        },
        /* Apply a revert: the checked inverse ops as an ordinary proposal, plus re-inserting
           any auth rows the original guard cascade removed (human authority — D8/D11). */
        applyRevert({ state, commit, dispatch }, { record, ops }) {
            return dispatch('applyCanvasOps', {
                ops,
                source: 'revert',
                label: `Revert: ${record.label}`,
            }).then(result => {
                if (result.ok && record.authRows && record.authRows.length) {
                    if (!Array.isArray(state.canvas.authorizations))
                        Vue.set(state.canvas, 'authorizations', []);
                    for (const entry of record.authRows) {
                        if (!state.canvas.authorizations.some(a => _.isEqual(a, entry.row))) {
                            const at = Math.min(entry.index, state.canvas.authorizations.length);
                            state.canvas.authorizations.splice(at, 0, _.cloneDeep(entry.row));
                        }
                    }
                }
                return result;
            });
        },
        resetState({ commit }) {
            commit('resetState');
        },
        setClientHeadersWsId({ commit }, workspaceId) {
            commit('setClientHeadersWsId', workspaceId);
        },
        setIsSaving({ commit }, payload) {
            commit('setIsSaving', payload);
        },
        updateModule({ commit }, payload) {
            commit('updateModule', payload);
        },
        loadCanvas(context, payload) {
            let canvas = payload.canvas
            if (canvas && canvas.information && Array.isArray(canvas.information))
                canvas.information.filter(x => x.type === 14 && !x.fields && Array.isArray(x.columns)).forEach(y => y.fields = y.columns)

            context.commit('setCanvas', canvas);
            context.commit('setBaseServer', payload.baseServer);
            context.commit('setVector', payload.vector);

            if (payload.vectorData != null) {
                context.commit('commitVector', payload.vectorData);
            }
        },
        async downloadSchema({ state }) {
            return await state.client.get(`/api/test/schema/${state.workspace.id}`);
        },
        async retrieveDashboard({ state }) {
            return await state.client.get('/api/workspace/studiodashboard');
        },
        deleteState(context, payload) {
            context.commit('removeState', payload.state.id);
            let flows = context.state.canvas.flows.filter(
                flow =>
                    flow.fromStateId == payload.state.id ||
                    flow.toStateId == payload.state.id ||
                    (flow.conditionalStates &&
                        flow.conditionalStates.some(
                            condition => condition.toStateId == payload.state.id
                        ))
            );
            flows.forEach(flow =>
                context.commit('removeFlow', {
                    targetFlowId: flow.id,
                    targetActivityId: flow.activityId
                }));
        },
        deleteFlow(context, payload) {
            context.commit('removeFlow', {
                targetFlowId: payload.flowId,
                targetActivityId: payload.activityId
            });
        },
        deleteActivity(context, payload) {
            context.commit('removeActivity', payload.activity.id);
        },
        deleteInformation(context, payload) {
            context.commit('removeInformation', payload.information.id);
        },
        confirmState(context, payload) {
            context.commit('commitState', payload.state);
        },
        confirmActivity(context, payload) {
            context.commit('commitActivity', payload);
        },
        updateActivity(context, payload) {
            context.commit('commitUpdateActivity', payload);
        },
        confirmInformation(context, payload) {
            context.commit('commitInformation', payload.information);
        },
        confirmWorkDesign(context, payload) {
            context.commit('commitWorkDesign', {
                workDesign: payload.workDesign,
                activityId: payload.activityId
            })
        },
        saveDiagram(context, payload) {
            context.commit('commitDiagram', payload);
        },
        async loadVectorSelection(context, payload) {
            return await context.state.client.get(
                `${context.state.baseServer || ''}/api/m/`)
        },
        testAutomationBlock(context, payload) {
            return context.state.client.post('/api/canvastest/notification', payload);
        },
        // design-time server-native compatibility verdict for a block graph (advisory;
        // the runtime re-classifies per canvas version)
        classifyAutomationNative(context, payload) {
            return context.state.client.post('/api/canvastest/automationnative', payload);
        },
        // design-time server-native verdict for an activity's FORM FORMULAS (classifies the
        // saved canvas; reports which fields force Python)
        classifyFormulaNative(context, payload) {
            return context.state.client.post('/api/canvastest/formulanative', payload);
        },
        // per-formula engine verdict for the editor badge: client (computes on-device under
        // bundle v4) / server (native server-side) / python (forces Tier-2/3). Live while the
        // editor is open; payload carries { activityId, formula, parentName }.
        classifyFormulaEngine(context, payload) {
            return context.state.client.post('/api/canvastest/formulaengine', {
                vectorId: context.state.vectorId,
                ...payload
            });
        },
        /* The spreadsheet function registry for the formula editor's hints — static per
           deploy; cached per designer session in formulaFunctions.js, so this fires at
           most once. */
        formulaFunctions(context) {
            return context.state.client.get('/api/canvastest/formulafunctions');
        },
        /* design-time per-FIELD sync indicator for the form designer: for each input, whether
           editing it round-trips to the server and why — "script"/"automation" (every edit
           syncs) or the surviving server-owned formula targets it feeds (post v4 ownership
           stripping). The designer posts the WORKING design + information and refreshes for the
           whole form whenever any field changes. payload: { activityId, design, moduleInformation } */
        classifySyncTriggers(context, payload) {
            return context.state.client.post('/api/canvastest/synctriggers', {
                vectorId: context.state.vectorId,
                ...payload
            });
        },
        async validateAutomation(context, payload) {
            return context.state.client.post('/api/studio/validateautomation', {
                'automationBlocks': payload,
                'vectorId': context.state.vector.id
            });
        },
        testAutomation(context, payload) {
            return context.state.client.post('/api/canvastest/notification', {
                'automation': payload,
                'vectorId': context.state.vectorId
            });
        },
        testDocument(context, payload) {
            return context.state.client.post('/api/canvastest/document', {
                'document': payload,
                'vectorId': context.state.vector.id
            });
        },
        // htmljs document path: read the typed DocumentDataModel for live preview, and store a bundle.
        docData(context, payload) {
            return context.state.client.post('/doc/data', payload);
        },
        docBundle(context, payload) {
            return context.state.client.post('/doc/bundle', payload);
        },
        docBundleGet(context, ref) {
            return context.state.client.get('/doc/bundle/' + ref);
        },
        // The htmljs designer's file-name check: evaluates the unsaved native formula server-side.
        docFileName(context, payload) {
            return context.state.client.post('/doc/filename', payload);
        },
        // Studio DEBUG only: byte-exact server PDF via the same render service the backend uses for
        // email/attachments. Not a user-facing FE download (the FE downloads client-side); this is the
        // designer's "compare against the real output" button. Sends the inline in-editor bundle so it
        // renders the CURRENT edits, not the last-saved template.
        docRender(context, payload) {
            return context.state.client.post('/doc/render', payload, { responseType: 'blob' });
        },
        // htmljs document path: AI-draft (or revise) a bundle from a natural-language brief.
        // Only ever invoked from the htmljs design modal — the draft lands in the open editor
        // for live-preview review; saving still goes through docBundle + the module save.
        teammateDocument(context, payload) {
            return context.state.client.post('/api/teammate/document', payload);
        },
        // htmljs document path: list a module's entries so the designer can pick a sample to preview.
        docEntries(context, payload) {
            return context.state.client.post('/api/workspace/list', {
                moduleId: payload.moduleId,
                listingId: payload.listingId,
                withHeader: false,
                currentPage: 0, // 0-indexed: page 1 would be empty for small modules
                pageSize: payload.pageSize || 50,
                filters: null,
                sorts: null,
                contextId: null
            });
        },
        summary(context, payload) {
            return context.state.client.get(`/api/canvastest/summary/${payload}`);
        },
        previewAutomation(context, payload) {
            return context.state.client.post('/api/canvastest/preview', {
                'automation': payload,
                'vectorId': context.state.vectorId
            });
        },
        plugins(context, payload) {
            return context.state.client.get('/api/studio/plugins');
        },
        plugin(context, payload) {
            return context.state.client.get(`/api/studio/plugin/${payload.project}/${payload.id}`);
        },
        loadPackages(context, payload) {
            return context.state.client.post('/api/p/list', {
                "pageSize": payload.pageSize || 6,
                "category": payload.category,
                "searchText": payload.searchText,
            });
        },
        loadPackageDetail(context, payload) {
            return context.state.client.get(`/api/p/${payload}`);
        },
        intention(context, payload) {
            payload.moduleId = context.state.vectorId.toString();
            return context.state.client.post('/api/ai/intention', payload);
        },
        processSchema(context, payload) {
            return context.state.client.post('/api/ai/schema', { schema: payload });
        },
        packModule(context, payload) {
            payload.moduleId = context.state.vectorId.toString();
            return context.state.client.post('/api/m/package', payload);
        },
        retrieveWorkspaceInformation(context, payload) {
            return context.state.client.get(`/api/workspace/update/${payload}`);
        },
        updateWorkspaceInformation(context, payload) {
            payload.update = true;
            return context.state.client.post('/api/workspace', payload);
        },
        /* Workspace-wide AI teammate instructions (appended to every GNey system prompt).
           payload: workspaceId (retrieve) / { workspaceId, instructions } (save). */
        retrieveTeammateInstructions(context, payload) {
            return context.state.client.get(`/api/workspace/TeammateInstructions?workspaceId=${payload}`);
        },
        saveTeammateInstructions(context, payload) {
            return context.state.client.post('/api/workspace/TeammateInstructions', payload);
        },
        /* SS03547: workspace "everyone" access policy - restrict the activity stream's "everyone"
           mode and calendars "for everyone" to admins. payload: workspaceId (retrieve) /
           { workspaceId, restrictActivityStream, restrictSharedCalendar } (save, admin-only). */
        /* Two-step verification for the workspace: the policy could be SET since SS05894 but
           never read back, so no screen could show the switch in its real position.
           payload: workspaceId. */
        retrieveTwoFactorStatus(context, payload) {
            return context.state.client.get(`/api/workspace/TwoFactorStatus?workspaceId=${payload}`);
        },
        saveEnforceTwoFactor(context, payload) {
            return context.state.client.post('/api/workspace/EnforceTwoFactor', payload);
        },
        retrieveEveryoneAccess(context, payload) {
            return context.state.client.get(`/api/workspace/EveryoneAccess?workspaceId=${payload}`);
        },
        saveEveryoneAccess(context, payload) {
            return context.state.client.post('/api/workspace/EveryoneAccess', payload);
        },
        async cloneWorkspace(context, payload) {
            // SS06388: as createWorkspace does - the copy's id reaches the token's workspace claim, which legacy
            // checks for the workspace-admin pages (Settings > General, People & seats), only on a token refresh.
            let result = await context.state.client.post('/api/workspace/clone', payload);
            await authorizationStore.dispatch('refreshToken');
            return result;
        },
        deleteWorkspace(context, payload) {
            return context.state.client.post('/api/workspace/delete', payload);
        },
        loadModuleHistory(context, payload) {
            return context.state.client.get(`/api/workspace/history/${payload.moduleId}`);
        },
        revertChange(context, payload) {
            return context.state.client.post(`/api/m/revert`, payload);
        },
        async testConfiguration(context, payload) {
            return await context.state.client.post('/api/studio/test', {
                'script': payload.item.script,
                'payload': payload.item.payload,
                'vectorId': context.state.vectorId,
                'actual': payload.item.actual,
                'logicService': true
            });
        },
        async saveVector(context, payload) {
            const request = {
                liveVectorAtRequest: context.state.vector,
                submittedVector: _.cloneDeep(context.state.vector),
                submittedVectorGeneration: context.state.vectorGeneration,
            };
            const moduleId = request.submittedVector && request.submittedVector.id;
            if (!moduleId) {
                return sendVectorSave(context, payload, request, null);
            }

            const key = moduleId.toString();
            const queue = vectorSaveQueues[key]
                || (vectorSaveQueues[key] = { tail: Promise.resolve(), waiting: null, updatedDate: null });
            const options = JSON.stringify(payload || null);
            if (queue.waiting && queue.waiting.options === options) {
                queue.waiting.request = request;
                return queue.waiting.promise;
            }

            const waiting = { options, request };
            waiting.promise = queue.tail.then(() => {
                if (queue.waiting === waiting) queue.waiting = null;
                return sendVectorSave(context, payload, waiting.request, queue);
            });
            queue.waiting = waiting;
            queue.tail = waiting.promise.catch(() => {});
            return waiting.promise;
        },
        async checkConsistency({ state }) {
            const vector = _.cloneDeep(state.vector);
            vector.dynamic.expansion = JSON.stringify(vector.extension);
            const result = await state.client.post('/api/studio/consistency-check', {
                vector
            });
            return result.data;
        },
        async loadModuleDesign({ state }, payload) {
            return await state.client.get(`/api/studio/${payload}`);
        },
        async loadAIVector(context, response) {
            let vm = this;
            let result = response;
            
            /* set module */
            let standardActivities = result.data.extension.standardActivities;
            Vue.set(result.data.extension, 'standardActivities', []);
            commons.standards.forEach(standard => {
                let found = standardActivities.find(x => x.id == standard.id);
                if (found) {
                    standardActivities = standardActivities.filter(item => item !== found);
                    result.data.extension.standardActivities.push(found);
                } else {
                    result.data.extension.standardActivities.push(standard);
                }
            });

            standardActivities.forEach(x => {
                if (result.data.extension.triggers
                    && result.data.extension.triggers.find(y => y.item == x.id)) {
                    result.data.extension.standardActivities.push(x);
                }
            });

            if (result.data.extension.connector) {
                result.data.extension.connectorText = JSON.stringify(result.data.extension.connector);
                delete result.data.extension.connector;
            }

            if (result.data.extension.mapping) {
                result.data.extension.mappingText = JSON.stringify(result.data.extension.mapping);
                delete result.data.extension.mapping;
            }

            context.commit('commitVector', result.data);
            commons.setChildMenu(context.state.workspace.menus, context.state.vector);
        },
        async loadVector(context, payload) {
            let vm = this;
            const loadSequence = ++vectorLoadSequence;
            const loadGeneration = context.state.vectorGeneration;
            const isStale = () => loadSequence !== vectorLoadSequence || context.state.vectorGeneration !== loadGeneration;
            let result = await context.state.client.get(`/api/studio/${payload}`);
            if (isStale()) return;
            /* load workspace */
            if (result.data.ownerWorkspaceId && (!context.state.workspace || context.state.workspace.id != result.data.ownerWorkspaceId)) {
                let workspaceResult = await context.state.client.get(`/api/workspace/${result.data.ownerWorkspaceId}?studio=studio`);
                if (isStale()) return;
                authorizationStore.dispatch('setRoles', workspaceResult.data.roles);
                commons.expandMenu(workspaceResult.data.menus, workspaceResult.data.vectors, true);
                context.commit('commitWorkspace', workspaceResult.data);
            }
            /* set module */
            if (result.data.extension.standardActivities == null) {
                result.data.extension.standardActivities = commons.standards;
            } else {
                let standardActivities = result.data.extension.standardActivities;
                Vue.set(result.data.extension, 'standardActivities', []);
                commons.standards.forEach(standard => {
                    let found = standardActivities.find(x => x.id == standard.id);
                    if (found) {
                        standardActivities = standardActivities.filter(item => item !== found);
                        result.data.extension.standardActivities.push(found);
                    } else {
                        result.data.extension.standardActivities.push(standard);
                    }
                });

                standardActivities.forEach(x => {
                    if (result.data.extension.triggers
                        && result.data.extension.triggers.find(y => y.item == x.id)) {
                        result.data.extension.standardActivities.push(x);
                    }
                });
            }

            if (result.data.extension.connector) {
                result.data.extension.connectorText = JSON.stringify(result.data.extension.connector);
                delete result.data.extension.connector;
            }

            if (result.data.extension.mapping) {
                result.data.extension.mappingText = JSON.stringify(result.data.extension.mapping);
                delete result.data.extension.mapping;
            }

            context.commit('commitVector', result.data);
            commons.setChildMenu(context.state.workspace.menus, context.state.vector);
        },
        async installPackage({ state }, payload) {
            return await state.client.post(`/api/p/install`, payload);
        },
        async findVariable(context, payload) {
            if (payload.moduleId.toString() == context.state.vector.id.toString()) {
                /* Source the current module from the LIVE working canvas (state.canvas), not the
                   state.vector snapshot. The design modals commit fields/activities into state.canvas
                   (commitInformation -> state.canvas.information), while state.vector.extension can be
                   a stale copy (e.g. updateModule replaces state.vector without relinking state.canvas).
                   Reading the snapshot omitted not-yet-pushed user fields, so the formula autocomplete
                   showed only the intrinsic system fields. */
                const extension = _.cloneDeep(context.state.canvas || context.state.vector.extension || {});
                const standardActivities = extension.standardActivities || [];
                const activities = extension.activities || [];

                standardActivities.forEach(standard => {
                    if (standard.design) {
                        commons.expandWorkDesign(extension.information, standard.design);
                        standard.classification = commons.getAllElements(null, standard.design);
                    }
                });

                activities.forEach(activity => {
                    if (activity.design) {
                        commons.expandWorkDesign(extension.information, activity.design);
                        activity.classification = commons.getAllElements(null, activity.design);
                    }
                });

                return {
                    id: context.state.vector.id,
                    emoji: context.state.vector.emoji,
                    name: context.state.vector.name,
                    standards: standardActivities,
                    activities: activities,
                    information: extension.information,
                    isUserCollection: context.state.vector.isUserCollection
                }
            }

            if (!context.state.modules[payload.moduleId]) {
                if(!context.state.modulePromises[payload.moduleId]) {
                    let promise = context.state.client.get(`/api/m/${payload.moduleId}`);
                    context.commit('setModulePromise', {
                        moduleId: payload.moduleId,
                        promise: promise
                    })
                    let result = await promise;
                    context.commit('registerModule', result.data);
                }

                let result = await context.state.modulePromises[payload.moduleId];
            }

            return context.state.modules[payload.moduleId];
        },
        setUsers(context, payload) {
            let users = payload.map((x) => (
                Object.assign(x, {
                    username: x.username || x.userName
                })
            ))

            context.commit('commitUsers', users)
        },
        loadList(context, payload) {
            if (context.state.workspace && context.state.workspace.menus && context.state.workspace.menus.length) {
                let result = _.findDeep(context.state.workspace.menus, x => (x.id === payload), {
                    childrenPath: 'children'
                })
                if (result && result.value && result.parent && result.parent.id && result.parent.type != 'group') {
                    context.commit('commitList', {
                        id: result.value.id,
                        name: result.value.name,
                        emoji: result.value.emoji,
                        parentId: result.parent.id,
                    })
                    return
                }
            }

            context.commit('commitList', null)
        },
        async addVector({ state, dispatch, commit }, payload) {
            try {
                state.vector = {
                    name: payload.name,
                    emoji: payload.emoji,
                    description: payload.description,
                    ownerWorkspaceId: state.workspace.id,
                    extension: {
                        standardActivities: commons.standards
                    },
                    dynamic: {},
                    audit: {}
                }
                const res = await dispatch('saveVector');
                await dispatch('loadVector', res.data.id);
                dispatch('updateWorkspaceMenus');
            } catch (err) {
                throw err;
            }
        },
        async deleteVector(context, payload) {
            try {
                /* reconstruct menus */
                let workspace = _.cloneDeep(context.state.workspace)
                function deleteMenu(menus, id) {
                    let menu = menus.find(x => x.id == id);
                    if (menu) {
                        menus.splice(menus.indexOf(menu), 1);
                    } else {
                        let menuDeep = _.findDeep(menus, x => (x.id == id), { childrenPath: 'children' })
                        if (menuDeep && menuDeep.value && menuDeep.parent && Array.isArray(menuDeep.parent.children)) {
                            menuDeep.parent.children.splice(menuDeep.parent.children.indexOf(menuDeep.value), 1);

                            if (menuDeep.parent.type == 'group' && menuDeep.parent.children.length === 0)
                                deleteMenu(menus, menuDeep.parent.id);
                        }
                    }
                }
                deleteMenu(workspace.menus, payload);
                /* remove vector */
                workspace.vectors = workspace.vectors.filter(x => x.id != payload);
                /* commit workspace */
                context.commit('commitWorkspace', workspace);
                /* save workspace */
                await context.dispatch('saveWorkspace');
                /* delete vector */
                await context.state.client.post('/api/studio/delete/', {
                    id: payload,
                });
                /* load workspace */
                await context.dispatch('loadWorkspace', {
                    id: context.getters.workspaceId,
                });
                /* reset vector */
                context.dispatch('resetVector');
            } catch (err) {
                throw err;
            }
        },
        initialVector({ state, commit }) {
            let vector = {
                name: null,
                emoji: null,
                description: null,
                ownerWorkspaceId: state.workspace.id,
                extension: {
                    standardActivities: commons.standards
                },
                dynamic: {},
                audit: {}
            }
            commit("commitVector", vector);
        },
        resetVector(context, payload) {
            context.commit('commitVector', null)
        },
        initialCanvas({ commit }) {
            commit('setCanvas', {
                information: [],
                states: [],
                flows: [],
                activities: [],
                menus: [],
                actions: [],
                documents: [],
                designer: {}
            });
        },
        async getFirstOrDefaultWorkspaceId({ dispatch }) {
            try {
                const res = await dispatch('searchWorkspace', { search: '' });
                if (Array.isArray(res) && res[0] && res[0].id) {
                    return res[0].id;
                } else {
                    throw 'error';
                }
            } catch(err) {
                console.error(err);
                throw {
                    status: 404,
                    error: 'No workspace found. Please contact your administrator.'
                };
            }
        },
        async loadWorkspace({ state, commit, dispatch }, payload) {
            try {
                let wsId = (payload && payload.id) || $cookies.get('wsId');
                if (!wsId) {
                    wsId = await dispatch('getFirstOrDefaultWorkspaceId');
                }
                let res = await state.client.get(`/api/workspace/${wsId}?studio=studio`);
                authorizationStore.dispatch('setRoles', res.data.roles);
                commit('commitWorkspace', res.data);
                commons.expandMenu(res.data.menus, res.data.vectors);
                return res.data;
            } catch(err) {
                throw err;
            }
        },
        async selectWorkspace(context, payload) {
            let workspaceResult = await context.state.client.get(`/api/workspace/${payload.id}?studio=studio`);
            authorizationStore.dispatch('setRoles', workspaceResult.data.roles);
            commons.expandMenu(workspaceResult.data.menus, workspaceResult.data.vectors);
            context.commit('commitWorkspace', workspaceResult.data);
            if (context.state.vector)
                commons.setChildMenu(context.state.workspace.menus, context.state.vector);
        },
        async searchWorkspace(context, payload) {
            const search = payload && payload.search ? encodeURIComponent(payload.search) : "";
            let result = await context.state.client.get(`/api/workspace?search=${search}&studio=studio`);
            return result.data;
        },
        async searchRecipient(context, payload) {
            let result = await context.state.client.get(`/api/studio/recipient?search=${payload.search}`);
            return result;
        },
        async filterElement(context, payload) {
            context.commit('filterElement', payload);
            let keyword = payload;
            if (keyword) {
                let lowerKeyword = keyword.toLowerCase();
                //if nothing, call AI
                let hasEntry = context.state.canvas.information.some(x => x.displayName.toLowerCase() == lowerKeyword)
                    || context.state.canvas.activities.some(x => x.name.toLowerCase() == lowerKeyword)
                    || context.state.canvas.standardActivities.some(x => x.name.toLowerCase() == lowerKeyword)
                    || context.state.canvas.documents.some(x => x.name.toLowerCase() == lowerKeyword)
                    || context.state.canvas.states.some(x => x.name.toLowerCase() == lowerKeyword);

                if (!hasEntry) {
                    let result = await context.state.client.get(`/api/ai?type=suggest&text=${encodeURIComponent(lowerKeyword)}`);
                    console.log(result.data);
                    let suggestions = [
                        ...result.data.user,
                        ...(result.data.suggest != null ? result.data.suggest : [])
                    ];

                    suggestions = suggestions.map(item => {
                        if (item['activity'] != null) {
                            return {
                                section: 'activity',
                                name: item.activity.name
                            }
                        } else if (item['state'] != null) {
                            return {
                                section: 'state',
                                name: item.state.name,
                                color: item.state.color
                            }
                        } else {
                            return {
                                section: 'information',
                                displayName: item.information.displayName,
                                type: item.information.type
                            }
                        }
                    });

                    suggestions = suggestions.slice(0, 5);

                    context.commit('suggestions', suggestions);

                } else {
                    context.commit('suggestions', []);
                }
            } else {
                context.commit('suggestions', []);
            }
        },
        async retrieveAuthorizationEntities(context, payload) {
            let result = await context.state.client.post(`/api/studio/authorizationselection`, payload);
            return result;
        },
        async saveWorkspace(context, payload) {
            if (context.state.workspace) {
                let menus = _.cloneDeep(context.state.workspace.menus);
                menus = commons.trimMenu(menus);
                return await context.state.client.post(`/api/workspace`, {
                    id: context.state.workspace.id,
                    menus,
                    lastUpdatedOn: context.state.workspace.lastUpdatedOn
                });
            }
        },
        async createWorkspace(context, payload) {
            let result = await context.state.client.post(`/api/workspace`, payload);
            await authorizationStore.dispatch('refreshToken');
            return result;
        },
        setWorkspaceName({ commit }, payload) {
            commit('setWorkspaceName', payload);
        },
        setWorkspaceLastUpdatedOn({ commit }, payload) {
            commit('commitWorkspaceLastUpdatedOn', payload);
        },
        async runScheduledEvent(context, payload) {
            let url = `api/ScheduledEventTrigger/run`;
            return await context.state.client.post(url, { id: payload._id });
        },
        async ask(context, payload) {
            let url = `api/studio/ask`;
            return await context.state.client.post(url, payload);
        },
        async loadNotifcationDashboard(context, payload) {
            let url = `/api/studio/notificationdashboard/${(payload && payload.moduleId) || context.state.vector.id}`;

            if(payload) {
                /* SS04944: built from whichever filters are set. The hand-rolled '&' chaining
                   only held for two parameters, and an unencoded search term carrying '&'
                   truncated the query. */
                const params = ['searchTemplate', 'searchStatus', 'searchDelivery']
                    .filter(key => payload[key] !== null
                        && payload[key] !== undefined
                        && payload[key] !== '')
                    .map(key => `${key}=${encodeURIComponent(payload[key])}`);

                if(params.length) {
                    url += `?${params.join('&')}`;
                }
            }

            let result = await context.state.client.get(url);
            return result;
        },
        async ai(context, payload) {
            let result = await context.state.client.get(`/api/ai?type=${payload.type}&text=${payload.text}`);
            return result;
        },
        async testFormula(context, payload) {
            /* validate a computed-field formula through the canvas test engine — the same
               evaluator the native runtime uses to compute the field, so design-time
               validation has parity with on-device evaluation. vectorId supplies the
               module/canvas context; information carries the working (possibly unsaved)
               field schema so references to not-yet-saved fields resolve.
               response: { valid: boolean, type: <runtimeInformationTypeId>, error?: string } */
            return await context.state.client.post('/api/canvastest/formula', {
                formula: payload.formula,
                vectorId: context.state.vectorId,
                information: payload.information || (context.state.canvas && context.state.canvas.information),
                /* set when validating a column formula: switches the engine into row context so
                   column('X') sibling references resolve against the table's columns (information) */
                parentId: payload.parentId || null,
                /* the table display name: lets a drill into the same table resolve as a per-row value */
                parentName: payload.parentName || null,
                /* the full working (unsaved) module schema, so references to not-yet-saved
                   fields/tables/columns resolve live without requiring a save first */
                moduleInformation: payload.moduleInformation || (context.state.canvas && context.state.canvas.information) || null
            });
        },
        async retrieveSerialNumber(context, payload) {
            let result = await context.state.client.get(`/api/studio/serialnumber/${context.state.vector.id}`);
            return result;
        },
        async saveSerialNumber(context, payload) {
            try {
                payload.moduleId = context.state.vector.id;
                let result = await context.state.client.post(`/api/studio/serialnumber`, payload);
                return result;
            } catch (err) {
                throw err;
            }
        },
        async loadInformation(context, payload) {
            let result = await context.state.client.get(`/api/studio/information?id=${payload.name}`);
            return result;
        },
        async loadClassification(context, payload) {
            let result = await context.state.client.post(`/api/studio/classification`, payload);
            return result;
        },
        updateAuthorization(context, payload) {
            context.commit('commitUpdateAuthorization', payload);
        },
        setVectorTrigger({ commit }, data) {
            commit('setVectorTrigger', data)
        },
        removeVectorTrigger({ commit }, id) {
            commit('removeVectorTrigger', id)
        },
        updateWorkspaceMenus({ state, commit }) {
            let workspace = _.cloneDeep(state.workspace);
            workspace.menus.push({
                id: state.vector.id,
                emoji: state.vector.emoji,
                name: state.vector.name,
                children: [],
                module: state.vector.id,
                ownerWorkspaceId: workspace.id,
                currentWorkspace: true,
                published: true,
            });
            workspace.vectors.push({
                id: state.vector.id,
                emoji: state.vector.emoji,
                name: state.vector.name,
                menus: [],
            });
            commit('commitWorkspace', workspace);
        },
        updateVectorCanvas({ commit }, data) {
            commit("commitVectorCanvas", data);
        },
        setAutoPush({ commit }, data) {
            commit("commitAutoPush", data);
        },
        setForceSubmit({ commit }, data) {
            commit("commitForceSubmit", data);
        },
        async loadModule({ state }, payload) {
            return await state.client.get(`/api/m/${payload}`);
        },
        async loadModuleInformation({ state }, payload) {
            try {
                const res = await state.client.get(`/api/m/${payload}`);
                return res.data.information;
            } catch (err) {
                throw err;
            }
        }
    }
});
